import type { FC } from 'react';

import { useState } from 'react';

import { trim } from '../../../utils/stringUtils';
import CloudinaryImage from '../CloudinaryImage';
import styles from './ProductBadge.module.scss';

interface Dimensions {
    width: number | 'auto';
    height: number | 'auto';
}

type ProductBadgeProps = {
    dimensions: Dimensions;
    url: string;
    alt?: string;
    focusable?: boolean;
    as?: 'div' | 'li';
} & ({ zoomable: true; zoomDimensions: Dimensions } | { zoomable: false });

const getTransformDimensions = (dimensions: Dimensions) => {
    const height = dimensions.height !== 'auto' ? `h_${dimensions.height}` : '';
    const width = dimensions.width !== 'auto' ? `w_${dimensions.width}` : '';

    const retinaHeight = dimensions.height !== 'auto' ? `h_${dimensions.height * 2}` : '';
    const retinaWidth = dimensions.width !== 'auto' ? `w_${dimensions.width * 2}` : '';

    const transformDimensions = trim(`${height},${width}`, ',');
    const transformDimensionsRetina = trim(`${retinaHeight},${retinaWidth}`, ',');
    return { desktop: transformDimensions, retina: transformDimensionsRetina };
};

const ProductBadge: FC<React.PropsWithChildren<ProductBadgeProps>> = (props) => {
    const cloudinaryDimensions = props.zoomable ? props.zoomDimensions : props.dimensions;
    const transformDimensions = getTransformDimensions(cloudinaryDimensions);

    const [isZoomed, setIsZoomed] = useState<boolean>(false);

    const zoomIn = () => {
        if (props.zoomable) {
            setIsZoomed(true);
        }
    };

    const zoomOut = () => {
        setIsZoomed(false);
    };

    const toggleZoom = () => {
        if (props.zoomable) {
            setIsZoomed((zoomed) => !zoomed);
        }
    };

    const currentDimensions = props.zoomable && isZoomed ? props.zoomDimensions : props.dimensions;

    const Component = props.as || 'div';

    return (
        <Component
            className={styles.ProductBadgeContainer}
            onMouseEnter={zoomIn}
            onMouseLeave={zoomOut}
            onFocus={zoomIn}
            onBlur={zoomOut}
            onTouchStart={toggleZoom}
        >
            <CloudinaryImage
                tabIndex={props.focusable ? 0 : undefined}
                cssClass={styles.ProductBadge}
                url={props.url}
                name={props.alt || ''}
                title={props.alt}
                transform={`fl_progressive,q_90,c_pad,g_center${
                    transformDimensions.desktop ? `,${transformDimensions.desktop}` : ''
                }`}
                retinaTransform={`fl_progressive,q_90,c_pad,g_center${
                    transformDimensions.retina ? `,${transformDimensions.retina}` : ''
                }`}
                width={currentDimensions.width === 'auto' ? undefined : currentDimensions.width}
                height={currentDimensions.height === 'auto' ? undefined : currentDimensions.height}
            />
        </Component>
    );
};

export default ProductBadge;
